import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { servicesAPI, useCustomers } from '../../_services';
import {
  Button,
  Divider,
  Grid,
  Header,
  Input,
  Segment,
  Table,
  Dropdown
} from 'semantic-ui-react';
import { history } from '../../_helpers';
import {
  copyToClipboard,
  getLocalStorage,
  dbUTCDatetimeToLocal
} from '../../utils';

export const ListCustomersContracts = () => {
  const customers = useCustomers();
  const [contracts, setContracts] = useState([]);
  const [nameFilter, setNameFilter] = useState('');
  const [documentsFilter, setDocumentsFilter] = useState([]);
  const [documentOptions, setDocumentOptions] = useState([]);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');
  const [sortLabel, setSortLabel] = useState('descending');
  const [sortedData, setSortedData] = useState([]);

  const sortTable = column => {
    setSortColumn(column);
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setSortLabel(sortLabel === 'ascending' ? 'descending' : 'ascending');

    localStorage.setItem('adminContractsSrtColumn', column);
    localStorage.setItem(
      'adminContractsSrtDir',
      sortDirection === 'asc' ? 'desc' : 'asc'
    );
  };

  useEffect(() => {
    if (!customers.length) return;

    servicesAPI.CustomerServices('getCustomersContracts').then(contracts => {
      const updatedContracts = contracts.map(contract => {
        const customer = customers.find(c => c.id === contract.customer_id);
        return {
          ...contract,
          active: customer?.status === 'Active',
          name: contract.forename + ' ' + contract.surname,
          document: (
            contract.document_name +
            ' - Rev. ' +
            contract.document_revision
          ).replace('Trojan Energy Ltd - ', '')
        };
      });
      setContracts(updatedContracts.filter(c => c.active));

      const documents = new Set(updatedContracts.map(c => c.document));
      const documentOptions = [...documents].sort().map((name, idx) => {
        return {
          key: name,
          value: name,
          text: name
        };
      });
      setDocumentOptions(documentOptions);
    });

    // Use user's preferred filter/sort settings otherwise defaults
    setNameFilter(getLocalStorage('adminContractsFltrName', ''));
    setDocumentsFilter(
      JSON.parse(getLocalStorage('adminContractsFltrDocs', '[]'))
    );

    sortTable(getLocalStorage('adminContractsSrtColumn', 'document'));
    const dir = getLocalStorage('adminContractsSrtDir', 'desc');
    setSortDirection(dir);
    setSortLabel(dir === 'asc' ? 'descending' : 'ascending');
  }, [customers]); // eslint-disable-line

  useEffect(() => {
    if (contracts.length === 0) return;

    const sorted =
      sortColumn && sortDirection
        ? _.orderBy(contracts, sortColumn, sortDirection)
        : contracts;

    setSortedData(
      sorted
        .filter(
          ({ name }) =>
            !nameFilter || name.toLowerCase().includes(nameFilter.toLowerCase())
        )
        .filter(
          ({ document }) =>
            documentsFilter.length === 0 || documentsFilter.includes(document)
        )
    );
  }, [contracts, nameFilter, documentsFilter, sortColumn, sortDirection]);

  const exportCSV = () => {
    // Create CSV file
    let csvContent =
      'Name, Document Name / Revision, Document Revision Timestamp, Agreed to T&Cs Timestamp\n';
    sortedData.forEach(contract => {
      csvContent += `${contract.name}, ${
        contract.document
      }, ${dbUTCDatetimeToLocal(
        contract.document_revision_datetime
      )}, ${dbUTCDatetimeToLocal(contract.agreed_to_terms_datetime)}\n`;
    });

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8,' });
    const objUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = 'trojan_contracts.csv';
    link.href = objUrl;
    link.click();
  };

  return (
    <Segment>
      <Grid stretched>
        <Grid.Row>
          <Grid.Column width={3}>
            <Header as="h2">Customer Contracts</Header>
          </Grid.Column>
          <Grid.Column floated="right" textAlign="right" width={3}>
            <Button
              onClick={() => {
                exportCSV();
              }}
              color="olive"
            >
              Export as CSV
            </Button>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={3}>
            <Input
              type="text"
              placeholder="filter name"
              icon={{
                name: 'close',
                link: true,
                onClick: () => {
                  setNameFilter('');
                  localStorage.setItem('adminContractsFltrName', '');
                }
              }}
              value={nameFilter}
              onChange={(event, { value }) => {
                setNameFilter(value);
                localStorage.setItem('adminContractsFltrName', value);
              }}
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <Dropdown
              placeholder="select documents"
              selection
              search
              options={documentOptions}
              multiple
              onChange={(e, { value }) => {
                setDocumentsFilter(value);
                localStorage.setItem(
                  'adminContractsFltrDocs',
                  JSON.stringify(value)
                );
              }}
              value={documentsFilter}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Divider hidden />
      <Grid>
        <Table sortable celled fixed>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                sorted={sortColumn === 'surname' ? sortLabel : null}
                onClick={() => sortTable('surname')}
              >
                Name
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'document' ? sortLabel : null}
                onClick={() => sortTable('document')}
              >
                Document Name / Revision
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={
                  sortColumn === 'document_revision_datetime' ? sortLabel : null
                }
                onClick={() => sortTable('document_revision_datetime')}
              >
                Document Revision Timestamp
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={
                  sortColumn === 'agreed_to_terms_datetime' ? sortLabel : null
                }
                onClick={() => sortTable('agreed_to_terms_datetime')}
              >
                Agreed to T&Cs Timestamp
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedData.map((d, idx) => (
              <Table.Row
                key={idx}
                onClick={() => {
                  copyToClipboard(d.customer_id);
                  history.push(`/admin/customer/${d.customer_id}`);
                }}
              >
                <Table.Cell>{d.name}</Table.Cell>
                <Table.Cell>{d.document}</Table.Cell>
                <Table.Cell>
                  {dbUTCDatetimeToLocal(d.document_revision_datetime)}
                </Table.Cell>
                <Table.Cell>
                  {dbUTCDatetimeToLocal(d.agreed_to_terms_datetime)}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Grid>
    </Segment>
  );
};
